/* PrimeCut shared dialog helper (After Audit): any <dialog> opened by a [data-pc-dialog-open="<id>"] control.
   Native showModal() provides focus containment and Escape; this adds backdrop/close-button closing, the page scroll
   lock used by the other PrimeCut dialogs, and returns focus to the control that opened the dialog. Opening or
   closing never changes page state, so selections made on the page are kept. */
(() => {
  'use strict';
  if (window.PrimeCutDialog) return;
  const openers = new WeakMap();

  function close(dialog) {
    if (!dialog) return;
    if (typeof dialog.close === 'function' && dialog.open) dialog.close();
    else if (dialog.hasAttribute('open')) {
      dialog.removeAttribute('open');
      dialog.dispatchEvent(new Event('close'));
    }
  }

  function prepare(dialog) {
    if (dialog.dataset.pcDlgReady) return;
    dialog.dataset.pcDlgReady = 'true';
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog || event.target.closest('[data-pc-dialog-close]')) close(dialog);
    });
    // Escape closes only this dialog. It is handled on keyup, with the keydown default cancelled, so the key never
    // reaches a cart drawer or menu underneath (those close on Escape keyup once focus is back inside them).
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
      }
    });
    dialog.addEventListener('keyup', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        close(dialog);
      }
    });
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
    });
    dialog.addEventListener('close', () => {
      if (!document.querySelector('dialog[open]')) document.documentElement.classList.remove('pc-scroll-lock');
      const opener = openers.get(dialog);
      if (opener && document.contains(opener) && typeof opener.focus === 'function') opener.focus({ preventScroll: true });
    });
  }

  function open(dialog, opener) {
    if (!dialog || dialog.open) return;
    prepare(dialog);
    openers.set(dialog, opener || document.activeElement);
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    dialog.scrollTop = 0;
    document.documentElement.classList.add('pc-scroll-lock');
    const first = dialog.querySelector('[data-pc-dialog-close]');
    if (first) first.focus({ preventScroll: true });
  }

  document.addEventListener('click', (event) => {
    // A control with data-pc-dialog-open="<id>", or a plain link to "#<id>" (FAQ answers are rich text and cannot
    // carry data attributes).
    const opener = event.target.closest('[data-pc-dialog-open], a[href^="#Pc"]');
    if (!opener) return;
    const id = opener.getAttribute('data-pc-dialog-open') || opener.getAttribute('href').slice(1);
    const dialog = document.getElementById(id);
    if (dialog && dialog.tagName !== 'DIALOG') return;
    if (!dialog) return;
    event.preventDefault();
    event.stopPropagation();
    open(dialog, opener);
  });

  window.PrimeCutDialog = { open, close };
})();

/* Photo lightbox (community gallery): any [data-pc-lb] button inside a section that has a [data-pc-lb-dialog].
   Opens through PrimeCutDialog, so Escape, the scroll lock, the page's scroll position and returning focus to the
   photo that was clicked all work the same way as the other dialogs. The large image is requested only when shown. */
(() => {
  'use strict';
  const calm = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  function setup(dialog) {
    if (dialog.dataset.pcLbReady) return dialog.pcLb;
    dialog.dataset.pcLbReady = 'true';
    const scope = dialog.closest('section') || document;
    const img = dialog.querySelector('[data-pc-lb-img]');
    const count = dialog.querySelector('[data-pc-lb-count]');
    const stage = dialog.querySelector('[data-pc-lb-stage]');
    const prev = dialog.querySelector('[data-pc-lb-prev]');
    const next = dialog.querySelector('[data-pc-lb-next]');
    let items = [];
    let index = 0;
    const show = (i) => {
      index = (i + items.length) % items.length;
      const item = items[index];
      img.classList.remove('is-loaded');
      img.alt = item.dataset.pcLbAlt || '';
      img.src = item.dataset.pcLb;
      if (count) count.textContent = `Photo ${index + 1} of ${items.length}`;
      const single = items.length < 2;
      if (prev) prev.hidden = single;
      if (next) next.hidden = single;
    };
    img.addEventListener('load', () => img.classList.add('is-loaded'));
    if (prev) prev.addEventListener('click', () => show(index - 1));
    if (next) next.addEventListener('click', () => show(index + 1));
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') show(index + 1);
      else if (event.key === 'ArrowLeft') show(index - 1);
    });
    // Swipe left / right on touch screens.
    let startX = null;
    let startY = 0;
    stage.addEventListener('touchstart', (event) => {
      startX = event.touches.length === 1 ? event.touches[0].clientX : null;
      startY = event.touches.length === 1 ? event.touches[0].clientY : 0;
    }, { passive: true });
    stage.addEventListener('touchend', (event) => {
      if (startX === null) return;
      const touch = event.changedTouches[0];
      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;
      startX = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) show(index + (dx < 0 ? 1 : -1));
    }, { passive: true });
    dialog.addEventListener('close', () => {
      img.removeAttribute('src');
    });
    dialog.pcLb = {
      open(opener) {
        items = [...scope.querySelectorAll('[data-pc-lb]')];
        show(Math.max(0, items.indexOf(opener)));
        window.PrimeCutDialog.open(dialog, opener);
      },
    };
    return dialog.pcLb;
  }
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-pc-lb]');
    if (!opener || !window.PrimeCutDialog) return;
    const scope = opener.closest('section') || document;
    const dialog = scope.querySelector('[data-pc-lb-dialog]');
    if (!dialog) return;
    event.preventDefault();
    setup(dialog).open(opener);
  });
  void calm;
})();
